import React, { useState } from 'react';
import config from '../../config';

const PowerBITest = () => {
  const [testResults, setTestResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const runDiagnostics = async () => {
    setLoading(true);
    setTestResults(null);

    try {
      // Use the proper API base URL from config
      const API_BASE_URL = config.API_BASE_URL;
      console.log('Using API Base URL:', API_BASE_URL);

      // Test 1: Check if API is working at all
      console.log('Testing basic API connectivity...');
      const basicResponse = await fetch(`${API_BASE_URL.replace('/api', '')}/api/db-health-check`);
      console.log('Basic API response status:', basicResponse.status);
      
      let basicData;
      try {
        const basicText = await basicResponse.text();
        basicData = JSON.parse(basicText);
      } catch (parseError) {
        console.error('Basic API returned non-JSON:', parseError);
        basicData = { error: 'Non-JSON response from basic API' };
      }

      // Test 2: PowerBI Health Check
      console.log('Testing PowerBI health endpoint...');
      const powerbiUrl = `${API_BASE_URL.replace('/api', '')}/api/powerbi/health`;
      console.log('Calling PowerBI URL:', powerbiUrl);
      
      const healthResponse = await fetch(powerbiUrl);
      console.log('PowerBI response status:', healthResponse.status);
      console.log('PowerBI response headers:', Object.fromEntries(healthResponse.headers.entries()));
      
      const responseText = await healthResponse.text();
      console.log('PowerBI raw response text:', responseText);
      
      let healthData;
      try {
        healthData = JSON.parse(responseText);
      } catch (parseError) {
        console.error('PowerBI JSON parse error:', parseError);
        healthData = { 
          error: 'Non-JSON response', 
          rawResponse: responseText.substring(0, 500),
          status: healthResponse.status,
          statusText: healthResponse.statusText
        };
      }
      
      setTestResults({
        apiConfig: {
          baseUrl: API_BASE_URL,
          environment: process.env.NODE_ENV || 'development'
        },
        basicAPI: {
          status: basicResponse.status,
          data: basicData
        },
        powerbiHealth: {
          status: healthResponse.status,
          data: healthData,
          rawResponse: responseText.substring(0, 500),
          url: powerbiUrl
        }
      });

    } catch (error) {
      console.error('PowerBI diagnostic error:', error);
      setTestResults({
        error: error.message,
        details: error.toString(),
        stack: error.stack
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h2>🔧 PowerBI Azure Diagnostics</h2>
      <p>This tool helps diagnose PowerBI configuration issues in Azure.</p>
      
      <button 
        onClick={runDiagnostics}
        disabled={loading}
        style={{
          padding: '12px 24px',
          backgroundColor: loading ? '#6c757d' : '#007bff',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: loading ? 'not-allowed' : 'pointer',
          fontSize: '16px',
          marginBottom: '20px'
        }}
      >
        {loading ? 'Running Diagnostics...' : 'Run PowerBI Diagnostics'}
      </button>

      {testResults && (
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '20px', 
          borderRadius: '8px',
          border: '1px solid #e9ecef'
        }}>
          <h3>Diagnostic Results</h3>
          
          {testResults.error ? (
            <div style={{ color: '#dc3545' }}>
              <h4>❌ Error</h4>
              <p><strong>Message:</strong> {testResults.error}</p>
              <p><strong>Details:</strong> {testResults.details}</p>
              {testResults.stack && (
                <details style={{ marginTop: '10px' }}>
                  <summary>Stack Trace</summary>
                  <pre style={{ fontSize: '12px', overflow: 'auto' }}>{testResults.stack}</pre>
                </details>
              )}
            </div>
          ) : (
            <>
              {/* API Configuration */}
              {testResults.apiConfig && (
                <div style={{ marginBottom: '20px' }}>
                  <h4>⚙️ API Configuration</h4>
                  <div style={{ 
                    backgroundColor: '#fff', 
                    padding: '10px', 
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    <p><strong>Environment:</strong> {testResults.apiConfig.environment}</p>
                    <p><strong>Base URL:</strong> {testResults.apiConfig.baseUrl}</p>
                  </div>
                </div>
              )}

              {/* Basic API Test */}
              {testResults.basicAPI && (
                <div style={{ marginBottom: '20px' }}>
                  <h4>🔌 Basic API Connectivity</h4>
                  <p><strong>Status:</strong> 
                    <span style={{ 
                      color: testResults.basicAPI.status === 200 ? '#28a745' : '#dc3545',
                      fontWeight: 'bold',
                      marginLeft: '8px'
                    }}>
                      {testResults.basicAPI.status}
                    </span>
                  </p>
                  {testResults.basicAPI.data && (
                    <div style={{ 
                      backgroundColor: '#fff', 
                      padding: '10px', 
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      <strong>Response:</strong> {JSON.stringify(testResults.basicAPI.data)}
                    </div>
                  )}
                </div>
              )}

              {/* PowerBI Health Check Results */}
              {testResults.powerbiHealth && (
                <div style={{ marginBottom: '20px' }}>
                  <h4>🏥 PowerBI Health Check</h4>
                  <p><strong>URL:</strong> {testResults.powerbiHealth.url}</p>
                  <p><strong>Status:</strong> 
                    <span style={{ 
                      color: testResults.powerbiHealth.status === 200 ? '#28a745' : '#dc3545',
                      fontWeight: 'bold',
                      marginLeft: '8px'
                    }}>
                      {testResults.powerbiHealth.status}
                    </span>
                  </p>
                  
                  {testResults.powerbiHealth.data.config && (
                    <div style={{ 
                      backgroundColor: '#fff', 
                      padding: '15px', 
                      borderRadius: '6px',
                      marginTop: '10px'
                    }}>
                      <h5>Configuration Status:</h5>
                      <ul style={{ listStyle: 'none', padding: 0 }}>
                        <li>✅ Client ID Set: {testResults.powerbiHealth.data.config.client_id_set ? 'Yes' : 'No'}</li>
                        <li>✅ Client Secret Set: {testResults.powerbiHealth.data.config.client_secret_set ? 'Yes' : 'No'}</li>
                        <li>✅ Tenant ID Set: {testResults.powerbiHealth.data.config.tenant_id_set ? 'Yes' : 'No'}</li>
                        <li>🔑 Tenant ID: {testResults.powerbiHealth.data.config.tenant_id_value}</li>
                        <li>🌐 Authority URL: {testResults.powerbiHealth.data.config.authority_url}</li>
                      </ul>
                      
                      {testResults.powerbiHealth.data.config.authentication && (
                        <div style={{ marginTop: '15px' }}>
                          <h5>Authentication Test:</h5>
                          <p><strong>Result:</strong> 
                            <span style={{ 
                              color: testResults.powerbiHealth.data.config.authentication === 'SUCCESS' ? '#28a745' : '#dc3545',
                              fontWeight: 'bold',
                              marginLeft: '8px'
                            }}>
                              {testResults.powerbiHealth.data.config.authentication}
                            </span>
                          </p>
                          
                          {testResults.powerbiHealth.data.config.error && (
                            <div style={{ 
                              backgroundColor: '#f8d7da', 
                              color: '#721c24',
                              padding: '10px',
                              borderRadius: '4px',
                              marginTop: '10px'
                            }}>
                              <strong>Error:</strong> {testResults.powerbiHealth.data.config.error}
                            </div>
                          )}
                          
                          {testResults.powerbiHealth.data.config.auth_error && (
                            <div style={{ 
                              backgroundColor: '#f8d7da', 
                              color: '#721c24',
                              padding: '10px',
                              borderRadius: '4px',
                              marginTop: '10px'
                            }}>
                              <strong>Auth Error:</strong> {testResults.powerbiHealth.data.config.auth_error}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {testResults.powerbiHealth.data.error && (
                    <div style={{ 
                      backgroundColor: '#f8d7da', 
                      color: '#721c24',
                      padding: '10px',
                      borderRadius: '4px',
                      marginTop: '10px'
                    }}>
                      <strong>PowerBI Endpoint Error:</strong> {testResults.powerbiHealth.data.error}
                      {testResults.powerbiHealth.data.rawResponse && (
                        <details style={{ marginTop: '10px' }}>
                          <summary>Raw Response (first 500 chars)</summary>
                          <pre style={{ fontSize: '10px', overflow: 'auto' }}>
                            {testResults.powerbiHealth.data.rawResponse}
                          </pre>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          
          {/* Raw Response for Debugging */}
          <details style={{ marginTop: '20px' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
              🔍 Raw Response Data (for debugging)
            </summary>
            <pre style={{ 
              backgroundColor: '#2d3748', 
              color: '#e2e8f0', 
              padding: '15px', 
              borderRadius: '6px',
              overflow: 'auto',
              fontSize: '12px',
              marginTop: '10px'
            }}>
              {JSON.stringify(testResults, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
};

export default PowerBITest; 